import { useEffect, useState, type FormEvent } from 'react';
import { LockKeyhole, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import { ApiError, validateInvite } from '../api';
import { Button, Field, TextInput } from './ui';
import { formatMoney } from '../utils';

export function AuthDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { login, register, settings, user } = useAuth();
  const { toast } = useUi();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteState, setInviteState] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [inviteAmount, setInviteAmount] = useState(0);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) onClose();
  }, [user, onClose]);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setInviteCode('');
    setInviteState('idle');
    setInviteAmount(0);
  }, [open, mode]);

  if (!open) return null;

  const checkInvite = async () => {
    if (!inviteCode.trim()) return toast('请先输入邀请码', 'error');
    setInviteBusy(true);
    setInviteState('checking');
    try {
      const result = await validateInvite(inviteCode);
      setInviteState(result.valid ? 'valid' : 'invalid');
      setInviteAmount(result.amountCents);
      toast(result.valid ? '邀请码有效' : '邀请码无效或已过期', result.valid ? 'success' : 'error');
    } catch (error) {
      setInviteState('invalid');
      setInviteAmount(0);
      toast(error instanceof ApiError ? error.message : '邀请码校验失败', 'error');
    } finally {
      setInviteBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mode === 'register' && inviteState !== 'valid') {
      return toast('请先校验邀请码', 'error');
    }
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(username, password);
        toast('登录成功', 'success');
      } else {
        await register(username, password, inviteCode, displayName || undefined);
        toast('注册成功，初始资金已到账', 'success');
      }
      onClose();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <form className="modal-card auth-card" onSubmit={submit}>
        <button type="button" className="icon-button modal-close" onClick={onClose} aria-label="关闭"><X size={18} /></button>
        <div className="auth-mark"><LockKeyhole size={24} /></div>
        <span className="eyebrow">MEMBER ACCESS</span>
        <h2>{mode === 'login' ? '成员登录' : '注册成员'}</h2>
        <p className="muted">{mode === 'login' ? '使用服务器游戏 ID 和密码进入。' : '首次注册会按系统设置发放初始资金。'}</p>
        <div className="segmented">
          <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>登录</button>
          <button type="button" className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>注册</button>
        </div>
        <Field label="游戏 ID" hint="2-20 位，中英文、数字或下划线">
          <TextInput value={username} onChange={event => setUsername(event.target.value)} minLength={2} maxLength={20} required autoFocus />
        </Field>
        {mode === 'register' && (
          <Field label="显示名称" hint="可选，默认与游戏 ID 相同">
            <TextInput value={displayName} onChange={event => setDisplayName(event.target.value)} maxLength={24} />
          </Field>
        )}
        {mode === 'register' && (
          <Field label="注册邀请码" hint="必须填写管理员提供且当前有效的邀请码">
            <div className="inline-field">
              <TextInput value={inviteCode} onChange={event => { setInviteCode(event.target.value); setInviteState('idle'); }} minLength={8} maxLength={64} required autoComplete="off" />
              <Button type="button" variant="default" loading={inviteBusy} onClick={() => void checkInvite()}>校验</Button>
            </div>
            {inviteState === 'valid' && <span className="invite-ok">邀请码有效，注册后发放 {formatMoney(inviteAmount, settings.currency)}</span>}
            {inviteState === 'invalid' && <span className="invite-error">邀请码无效或已过期</span>}
          </Field>
        )}
        <Field label="密码" hint="至少 4 位">
          <TextInput type="password" value={password} onChange={event => setPassword(event.target.value)} minLength={4} maxLength={128} required />
        </Field>
        {mode === 'register' && !settings.registrationEnabled && (
          <p className="form-note danger-note">当前系统已关闭注册，请联系管理员。</p>
        )}
        <Button type="submit" variant="primary" className="wide" loading={busy} disabled={mode === 'register' && (!settings.registrationEnabled || inviteState !== 'valid')}>
          {mode === 'login' ? '进入委托中心' : '创建账号'}
        </Button>
      </form>
    </div>
  );
}
